let cachedLogoBuffer = null;

const MAX_LOGO_BYTES = 80_000;
const MAX_LOGO_PX = 128;

async function optimizeLogoBuffer(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength <= MAX_LOGO_BYTES) return arrayBuffer;
  if (typeof document === 'undefined') return null;

  return new Promise((resolve) => {
    try {
      const blob = new Blob([arrayBuffer], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(MAX_LOGO_PX / img.width, MAX_LOGO_PX / img.height, 1);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          async (optimized) => {
            URL.revokeObjectURL(url);
            if (!optimized) {
              resolve(null);
              return;
            }
            resolve(await optimized.arrayBuffer());
          },
          'image/png',
          0.85,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    } catch {
      resolve(null);
    }
  });
}

export async function loadBrandLogoBuffer(baseUrl = '') {
  if (cachedLogoBuffer) return cachedLogoBuffer;
  try {
    const prefix = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const resp = await fetch(`${prefix}/de-excel-logo.png`);
    if (!resp.ok) return null;
    let buf = await resp.arrayBuffer();
    if (buf.byteLength > MAX_LOGO_BYTES) {
      const optimized = await optimizeLogoBuffer(buf);
      buf = optimized || null;
    }
    if (!buf || buf.byteLength > MAX_LOGO_BYTES * 2) {
      // eslint-disable-next-line no-console
      console.warn('[ExcelExport] Logo skipped — file too large for safe XLSX embedding');
      return null;
    }
    cachedLogoBuffer = buf;
    return cachedLogoBuffer;
  } catch {
    return null;
  }
}

export function computeFinancialHealth(summary = {}, analytics = {}) {
  const failedRatio = summary.count ? summary.failedCount / summary.count : 0;
  const netPositive = summary.netBalance >= 0;
  let score = 100;
  if (failedRatio > 0.05) score -= 25;
  if (failedRatio > 0.15) score -= 25;
  if (summary.reversedCount > 0) score -= 10;
  if (!netPositive) score -= 15;
  if (summary.count === 0) score = 0;

  let label = 'HEALTHY';
  let tone = 'good';
  if (score < 70) {
    label = 'ATTENTION REQUIRED';
    tone = 'warn';
  }
  if (score < 45) {
    label = 'CRITICAL';
    tone = 'bad';
  }

  return { score: Math.max(0, Math.min(100, score)), label, tone, netPositive, failedRatio };
}

function fmtInr(amount) {
  return Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export function computeValidationChecks(summary = {}, analytics = {}, rows = []) {
  const sumCredits = rows.reduce((s, r) => s + (r.credit || 0), 0);
  const sumDebits = rows.reduce((s, r) => s + (r.debit || 0), 0);
  const sumGst = rows.reduce((s, r) => s + (r.gst || 0), 0);
  const netFromRows = sumCredits - sumDebits;

  const checks = [
    {
      name: 'Credit total matches ledger',
      pass: Math.abs(sumCredits - summary.totalCredits) < 1,
      detail: Math.abs(sumCredits - summary.totalCredits) < 1
        ? `Verified · ₹${fmtInr(summary.totalCredits)}`
        : `Mismatch · ledger ₹${fmtInr(summary.totalCredits)}, rows ₹${fmtInr(sumCredits)}`,
    },
    {
      name: 'Debit total matches ledger',
      pass: Math.abs(sumDebits - summary.totalDebits) < 1,
      detail: Math.abs(sumDebits - summary.totalDebits) < 1
        ? `Verified · ₹${fmtInr(summary.totalDebits)}`
        : `Mismatch · ledger ₹${fmtInr(summary.totalDebits)}, rows ₹${fmtInr(sumDebits)}`,
    },
    {
      name: 'Net balance reconciliation',
      pass: Math.abs(netFromRows - summary.netBalance) < 1,
      detail: Math.abs(netFromRows - summary.netBalance) < 1
        ? `Net · ₹${fmtInr(summary.netBalance)} verified`
        : `Variance · ledger ₹${fmtInr(summary.netBalance)}, rows ₹${fmtInr(netFromRows)}`,
    },
    {
      name: 'GST aggregation',
      pass: Math.abs(sumGst - analytics.totalGst) < 1,
      detail: Math.abs(sumGst - analytics.totalGst) < 1
        ? `GST · ₹${fmtInr(analytics.totalGst)} at 18%`
        : `Mismatch · computed ₹${fmtInr(sumGst)}, report ₹${fmtInr(analytics.totalGst)}`,
    },
    {
      name: 'Transaction count',
      pass: rows.length === summary.count,
      detail: rows.length === summary.count
        ? `${summary.count} transactions in period`
        : `Count mismatch · ledger ${summary.count}, rows ${rows.length}`,
    },
  ];

  const allPass = checks.every((c) => c.pass);
  return { checks, allPass, status: allPass ? 'RECONCILED' : 'VARIANCE DETECTED' };
}

/** ASCII-safe bar for Excel compatibility (no block unicode) */
export function buildSparkBar(value, max, width = 12) {
  if (!max || !value) return '.'.repeat(width);
  const filled = Math.max(1, Math.round((value / max) * width));
  return '#'.repeat(filled) + '.'.repeat(Math.max(0, width - filled));
}
